"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Loader2,
  Plus,
  DollarSign,
  User,
  Building,
  FileText,
  X,
  ArrowDownCircle,
  ArrowUpCircle,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-hot-toast";
import pettyCashService from "../apis/pettycash";

const PETTY_CASH_DEPARTMENTS = [
  "Bar",
  "Kitchen",
  "Poker Room",
  "4Common",
  "Brownsville",
];

const getLoginDisplayName = (currentUser) => {
  try {
    const profileRaw = localStorage.getItem("profile");
    if (profileRaw) {
      const profile = JSON.parse(profileRaw);
      if (profile?.name?.trim()) return profile.name.trim();
    }
  } catch (_) {
    /* ignore */
  }

  if (currentUser?.name?.trim()) return currentUser.name.trim();
  if (currentUser?.username?.trim()) return currentUser.username.trim();

  const storedUsername = (localStorage.getItem("username") || "").trim();
  if (storedUsername) return storedUsername;

  return "";
};

const resolveLoginFullName = (employees, currentUser) => {
  const loginName = getLoginDisplayName(currentUser);
  if (!loginName) return "";

  const all = (employees || []).filter(Boolean);
  const loginLower = loginName.toLowerCase();

  const exact = all.find((emp) => emp.toLowerCase() === loginLower);
  if (exact) return exact;

  const startsWithWord = all.find((emp) =>
    emp.toLowerCase().startsWith(`${loginLower} `)
  );
  if (startsWithWord) return startsWithWord;

  const startsWith = all.find((emp) => {
    const empLower = emp.toLowerCase();
    return empLower.startsWith(`${loginLower} `) || empLower.startsWith(`${loginLower}`);
  });
  if (startsWith && startsWith.toLowerCase() !== loginLower) {
    if (startsWith.length > loginName.length) return startsWith;
  }

  const firstToken = loginLower.split(/\s+/)[0];
  const byFirstName = all.find((emp) => {
    const empFirst = emp.toLowerCase().split(/\s+/)[0];
    return empFirst === firstToken;
  });
  if (byFirstName) return byFirstName;

  return loginName;
};

const buildEmployeeOptions = (employees, currentUser, search = "") => {
  const all = [...(employees || [])].filter(Boolean);
  const loginFullName = resolveLoginFullName(all, currentUser);

  let ordered;
  if (loginFullName) {
    const rest = all
      .filter((emp) => emp.toLowerCase() !== loginFullName.toLowerCase())
      .sort((a, b) => a.localeCompare(b));
    ordered = [loginFullName, ...rest];
  } else {
    ordered = all.sort((a, b) => a.localeCompare(b));
  }

  if (!search.trim()) return ordered;
  const searchLower = search.toLowerCase();
  return ordered.filter((emp) => emp.toLowerCase().includes(searchLower));
};

export function InlinePettyCashAddBar({
  onAddTransaction,
  currentBalance = 0,
  currentUser = null,
  isLoading = false,
}) {
  const [transactionType, setTransactionType] = useState("withdraw"); // "withdraw" | "deposit"
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEmpDropdownOpen, setIsEmpDropdownOpen] = useState(false);
  const [highlightedEmpIndex, setHighlightedEmpIndex] = useState(0);

  const [deptSearch, setDeptSearch] = useState("");
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
  const [highlightedDeptIndex, setHighlightedDeptIndex] = useState(0);

  // Input refs for automatic keyboard flow
  const amountInputRef = useRef(null);
  const empInputRef = useRef(null);
  const empDropdownRef = useRef(null);
  const empListRef = useRef(null);
  const deptInputRef = useRef(null);
  const deptDropdownRef = useRef(null);
  const deptListRef = useRef(null);
  const notesInputRef = useRef(null);

  const isDeposit = transactionType === "deposit";
  const numericAmount = parseFloat(amount) || 0;
  const isInsufficientFunds = !isDeposit && numericAmount > currentBalance;

  // Load employees list on mount
  useEffect(() => {
    let isMounted = true;
    pettyCashService
      .getEmployees()
      .then((res) => {
        if (isMounted && res?.success && Array.isArray(res.data)) {
          setEmployees(res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to load employees for petty cash inline bar:", err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Filtered and prioritized employees
  const filteredEmployees = useMemo(() => {
    return buildEmployeeOptions(employees, currentUser, searchTerm);
  }, [employees, currentUser, searchTerm]);

  // Filtered departments
  const filteredDepartments = useMemo(() => {
    if (!deptSearch.trim()) return PETTY_CASH_DEPARTMENTS;
    const term = deptSearch.trim().toLowerCase();
    return PETTY_CASH_DEPARTMENTS.filter((d) => d.toLowerCase().includes(term));
  }, [deptSearch]);

  // Reset highlight index when lists change
  useEffect(() => {
    setHighlightedEmpIndex(0);
  }, [filteredEmployees.length]);

  useEffect(() => {
    setHighlightedDeptIndex(0);
  }, [filteredDepartments.length]);

  // Auto-scroll for Employee dropdown
  useEffect(() => {
    if (isEmpDropdownOpen && empListRef.current) {
      const container = empListRef.current;
      const highlightedEl = container.querySelector(
        `[data-index="${highlightedEmpIndex}"]`
      );
      if (highlightedEl) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elTop = highlightedEl.offsetTop;
        const elBottom = elTop + highlightedEl.offsetHeight;

        if (elTop < containerTop) {
          container.scrollTop = elTop;
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight;
        }
      }
    }
  }, [highlightedEmpIndex, isEmpDropdownOpen]);

  // Auto-scroll for Department dropdown
  useEffect(() => {
    if (isDeptDropdownOpen && deptListRef.current) {
      const container = deptListRef.current;
      const highlightedEl = container.querySelector(
        `[data-index="${highlightedDeptIndex}"]`
      );
      if (highlightedEl) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elTop = highlightedEl.offsetTop;
        const elBottom = elTop + highlightedEl.offsetHeight;

        if (elTop < containerTop) {
          container.scrollTop = elTop;
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight;
        }
      }
    }
  }, [highlightedDeptIndex, isDeptDropdownOpen]);

  // Click outside listener for dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (empDropdownRef.current && !empDropdownRef.current.contains(e.target)) {
        setIsEmpDropdownOpen(false);
        setSearchTerm("");
      }
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(e.target)) {
        setIsDeptDropdownOpen(false);
        setDeptSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectEmployee = useCallback((empName) => {
    setName(empName);
    setSearchTerm("");
    setIsEmpDropdownOpen(false);
    // Smoothly focus Department input next
    setTimeout(() => {
      deptInputRef.current?.focus();
    }, 50);
  }, []);

  const selectDepartment = useCallback((dept) => {
    setDepartment(dept);
    setDeptSearch("");
    setIsDeptDropdownOpen(false);
    // Smoothly focus Notes input next
    setTimeout(() => {
      notesInputRef.current?.focus();
    }, 50);
  }, []);

  const handleTypeChange = (type) => {
    setTransactionType(type);
    if (type === "deposit") {
      setName("");
      setSearchTerm("");
      setIsEmpDropdownOpen(false);
    }
    setTimeout(() => {
      amountInputRef.current?.focus();
    }, 50);
  };

  // Keyboard navigation for Amount Input
  const handleAmountKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      if (!isDeposit) {
        if (numericAmount > currentBalance) {
          toast.error("Insufficient funds for this withdrawal");
          return;
        }
        empInputRef.current?.focus();
        setIsEmpDropdownOpen(true);
      } else {
        deptInputRef.current?.focus();
        setIsDeptDropdownOpen(true);
      }
    }
  };

  // Keyboard navigation for Employee Dropdown
  const handleEmpKeyDown = (e) => {
    if (isEmpDropdownOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedEmpIndex((prev) => (prev + 1) % filteredEmployees.length);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedEmpIndex((prev) =>
            prev <= 0 ? filteredEmployees.length - 1 : prev - 1
          );
        }
        return;
      }
      if (e.key === "PageDown") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedEmpIndex((prev) =>
            Math.min(filteredEmployees.length - 1, prev + 5)
          );
        }
        return;
      }
      if (e.key === "PageUp") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedEmpIndex((prev) => Math.max(0, prev - 5));
        }
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedEmpIndex(0);
        }
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedEmpIndex(filteredEmployees.length - 1);
        }
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (filteredEmployees.length > 0) {
          e.preventDefault();
          const selected = filteredEmployees[highlightedEmpIndex] || filteredEmployees[0];
          selectEmployee(selected);
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setIsEmpDropdownOpen(false);
        setSearchTerm("");
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (name) {
        deptInputRef.current?.focus();
      } else {
        setIsEmpDropdownOpen(true);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsEmpDropdownOpen(true);
    }
  };

  // Keyboard navigation for Department Dropdown
  const handleDeptKeyDown = (e) => {
    if (isDeptDropdownOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (filteredDepartments.length > 0) {
          setHighlightedDeptIndex((prev) => (prev + 1) % filteredDepartments.length);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (filteredDepartments.length > 0) {
          setHighlightedDeptIndex((prev) =>
            prev <= 0 ? filteredDepartments.length - 1 : prev - 1
          );
        }
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (filteredDepartments.length > 0) {
          e.preventDefault();
          const selected = filteredDepartments[highlightedDeptIndex] || filteredDepartments[0];
          selectDepartment(selected);
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setIsDeptDropdownOpen(false);
        setDeptSearch("");
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (department) {
        notesInputRef.current?.focus();
      } else {
        setIsDeptDropdownOpen(true);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsDeptDropdownOpen(true);
    }
  };

  // Keyboard navigation for Notes Input
  const handleNotesKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      toast.error("Please enter a valid amount");
      amountInputRef.current?.focus();
      return;
    }

    if (!isDeposit) {
      if (numericAmount > currentBalance) {
        toast.error("Insufficient funds for this withdrawal");
        amountInputRef.current?.focus();
        return;
      }
      if (!name.trim()) {
        toast.error("Please enter the name of the person receiving cash");
        empInputRef.current?.focus();
        setIsEmpDropdownOpen(true);
        return;
      }
    }

    if (!department) {
      toast.error("Please select a department");
      deptInputRef.current?.focus();
      setIsDeptDropdownOpen(true);
      return;
    }

    if (!notes.trim()) {
      toast.error("Please provide transaction details");
      notesInputRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        transactionType,
        amount: numericAmount,
        name: isDeposit ? "" : name.trim(),
        department,
        notes: notes.trim(),
        receipts: [],
      };

      const success = await onAddTransaction(payload);
      if (success) {
        // Reset form fields
        setAmount("");
        setName("");
        setDepartment("");
        setNotes("");
        setSearchTerm("");
        setDeptSearch("");
        setIsEmpDropdownOpen(false);
        setIsDeptDropdownOpen(false);

        // Return focus to amount input
        setTimeout(() => {
          amountInputRef.current?.focus();
        }, 80);
      }
    } catch (error) {
      console.error("Inline petty cash submit error:", error);
      toast.error(error.message || "Failed to add transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || isLoading;
  const isFormValid =
    numericAmount > 0 &&
    (isDeposit || (Boolean(name.trim()) && !isInsufficientFunds)) &&
    Boolean(department) &&
    Boolean(notes.trim());

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 mb-8 transition-all">
      {/* Header bar: Title, Type Switcher & Live Balance */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
            Add Petty Cash Transaction
          </h2>
          <span className="hidden md:flex items-center gap-1 text-[11px] text-gray-400 ml-2">
            <span>Press</span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-300 text-[10px] font-mono text-gray-700">
              Enter ↵
            </kbd>
            <span>to navigate / save</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Segmented Type Toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs font-medium">
            <button
              type="button"
              onClick={() => handleTypeChange("withdraw")}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${!isDeposit
                  ? "bg-rose-500 text-white font-semibold shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <ArrowDownCircle size={14} />
              <span>Withdrawal</span>
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange("deposit")}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${isDeposit
                  ? "bg-emerald-600 text-white font-semibold shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <ArrowUpCircle size={14} />
              <span>Deposit</span>
            </button>
          </div>

          {/* Balance Pill */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${isInsufficientFunds
                ? "bg-rose-50 border-rose-300 text-rose-700 animate-pulse"
                : "bg-blue-50 border-blue-200 text-blue-700"
              }`}
          >
            <span className="text-[10px] uppercase tracking-wider opacity-75">
              Available
            </span>
            <span>
              ${Number(currentBalance || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Insufficient Funds Warning */}
      {isInsufficientFunds && (
        <div className="mb-3.5 p-2.5 rounded-lg bg-rose-50 border border-rose-200 flex items-center gap-2 text-xs text-rose-700">
          <AlertCircle size={15} className="shrink-0 text-rose-500" />
          <span>
            Requested withdrawal of <strong>${numericAmount.toFixed(2)}</strong> exceeds live available balance of{" "}
            <strong>${Number(currentBalance || 0).toFixed(2)}</strong>.
          </span>
        </div>
      )}

      {/* Form Fields */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
          {/* 1. Amount */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <DollarSign size={15} />
              </div>
              <Input
                ref={amountInputRef}
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={handleAmountKeyDown}
                disabled={busy}
                className={`pl-8 text-sm font-semibold h-10 ${isInsufficientFunds ? "border-rose-400 focus:ring-rose-400" : ""
                  }`}
              />
            </div>
          </div>

          {/* 2. Cash Given To (Only for Withdrawal) */}
          {!isDeposit && (
            <div className="lg:col-span-3 relative" ref={empDropdownRef}>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <User size={12} className="text-gray-400" />
                  <span>Cash Given To</span>
                  <span className="text-red-500">*</span>
                </span>
                <span className="text-[10px] text-gray-400 font-normal">
                  (↑/↓ to navigate)
                </span>
              </label>

              <div className="relative">
                <Input
                  ref={empInputRef}
                  type="text"
                  value={isEmpDropdownOpen ? searchTerm : name}
                  placeholder="Select employee..."
                  onFocus={() => {
                    setSearchTerm(name);
                    setIsEmpDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsEmpDropdownOpen(true);
                  }}
                  onKeyDown={handleEmpKeyDown}
                  disabled={busy}
                  className="pr-8 text-xs h-10"
                />
                <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center gap-1">
                  {name ? (
                    <button
                      type="button"
                      onClick={() => {
                        setName("");
                        setSearchTerm("");
                        empInputRef.current?.focus();
                      }}
                      className="text-gray-400 hover:text-gray-600 p-0.5"
                    >
                      <X size={13} />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setIsEmpDropdownOpen((prev) => !prev)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
              </div>

              {/* Autocomplete Dropdown */}
              {isEmpDropdownOpen && (
                <div
                  ref={empListRef}
                  className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-y-auto py-1"
                >
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map((emp, index) => {
                      const isHighlighted = index === highlightedEmpIndex;
                      const isSelected = name === emp;

                      return (
                        <button
                          key={emp}
                          data-index={index}
                          type="button"
                          onClick={() => selectEmployee(emp)}
                          onMouseEnter={() => setHighlightedEmpIndex(index)}
                          className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${isHighlighted
                              ? "bg-gray-100 text-gray-900 font-semibold"
                              : isSelected
                                ? "bg-yellow-50 text-yellow-900 font-medium"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          <div className="flex items-center gap-2 truncate">
                            <User size={13} className="text-gray-400 shrink-0" />
                            <span className="truncate">{emp}</span>
                          </div>
                          {isSelected && (
                            <span className="text-[10px] text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded font-semibold shrink-0">
                              Selected
                            </span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">
                      No matching employees found
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 3. Department Searchable Dropdown */}
          <div
            className={!isDeposit ? "lg:col-span-2 relative" : "lg:col-span-3 relative"}
            ref={deptDropdownRef}
          >
            <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Building size={12} className="text-gray-400" />
                <span>Department</span>
                <span className="text-red-500">*</span>
              </span>
              <span className="text-[10px] text-gray-400 font-normal">
                (↑/↓)
              </span>
            </label>

            <div className="relative">
              <Input
                ref={deptInputRef}
                type="text"
                value={isDeptDropdownOpen ? deptSearch : department}
                placeholder="Select dept..."
                onFocus={() => {
                  setDeptSearch(department);
                  setIsDeptDropdownOpen(true);
                }}
                onChange={(e) => {
                  setDeptSearch(e.target.value);
                  setIsDeptDropdownOpen(true);
                }}
                onKeyDown={handleDeptKeyDown}
                disabled={busy}
                className="pr-8 text-xs h-10 font-medium"
              />
              <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center gap-1">
                {department ? (
                  <button
                    type="button"
                    onClick={() => {
                      setDepartment("");
                      setDeptSearch("");
                      deptInputRef.current?.focus();
                    }}
                    className="text-gray-400 hover:text-gray-600 p-0.5"
                  >
                    <X size={13} />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setIsDeptDropdownOpen((prev) => !prev)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
            </div>

            {/* Department Dropdown List */}
            {isDeptDropdownOpen && (
              <div
                ref={deptListRef}
                className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-y-auto py-1"
              >
                {filteredDepartments.length > 0 ? (
                  filteredDepartments.map((dept, index) => {
                    const isHighlighted = index === highlightedDeptIndex;
                    const isSelected = department === dept;

                    return (
                      <button
                        key={dept}
                        data-index={index}
                        type="button"
                        onClick={() => selectDepartment(dept)}
                        onMouseEnter={() => setHighlightedDeptIndex(index)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${isHighlighted
                            ? "bg-gray-100 text-gray-900 font-semibold"
                            : isSelected
                              ? "bg-yellow-50 text-yellow-900 font-medium"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        <span className="truncate">{dept}</span>
                        {isSelected && (
                          <span className="text-[10px] text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded font-semibold shrink-0">
                            Selected
                          </span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-xs text-gray-400 text-center">
                    No matching department
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 4. Transaction Details / Notes */}
          <div className={!isDeposit ? "lg:col-span-3" : "lg:col-span-5"}>
            <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <FileText size={12} className="text-gray-400" />
              <span>Transaction Details</span>
              <span className="text-red-500">*</span>
            </label>
            <Input
              ref={notesInputRef}
              type="text"
              placeholder="e.g. Office supplies, McAllen maintenance..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={handleNotesKeyDown}
              disabled={busy}
              className="text-sm h-10"
            />
          </div>

          {/* 5. Submit Button */}
          <div className="lg:col-span-2 pt-1 lg:pt-0">
            <Button
              type="submit"
              disabled={busy || !isFormValid}
              className={`w-full h-10 px-4 font-semibold text-xs uppercase tracking-wider transition-all shadow-xs ${isDeposit
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-yellow-400 hover:bg-yellow-500 text-black"
                }`}
            >
              {busy ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus className="mr-1.5 h-4 w-4 stroke-[2.5]" />
                  <span>{isDeposit ? "Add Deposit" : "Add Withdrawal"}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
